import { isBoolean, pad } from 'lodash';
import cloneDeep from 'lodash/cloneDeep';
import tinycolor from 'tinycolor2';
import {
    GroupRoleType,
    IBackgroundLayerProps,
    IBrushLayerProps,
    IChartLayerProps,
    IGroupLayerProps,
    IImageLayerProps,
    IImageTransform,
    ILayerProps,
    IMainLayerProps,
    IMarkupLayerProps,
    IMediaLayerProps,
    IPuzzleLayerProps,
    IQrCodeLayerProps,
    ITableLayerProps,
    ITextLayerProps,
    IVectorLayerProps,
    IWatermarkLayerProps,
    LayerType,
    MaskType,
} from '@/version-types';
import { ITopStruct } from '../server-template-types';
import {
    checkTableLayerPropsIsVersion15_0_0,
    IMagnifierLayerProps,
    IServerBgLayerProps,
    IServerBrushLayerProps,
    IServerChartLayerProps,
    IServerGroupLayerProps,
    IServerImageLayerProps,
    IServerLayerProps,
    IServerMainLayerProps,
    IServerMarkupLayerProps,
    IServerMediaLayerProps,
    IServerPuzzleLayerProps,
    IServerQrCodeLayerProps,
    IServerTableLayerProps,
    IServerTableLayerProps__15_0_0,
    IServerVectorLayerProps,
    IServerWatermarkLayerProps,
} from '../server-template-types/layer';
import { validateTemplate } from '../server-template-types/validate-version';
import { LATEST_VERSION, VERSION } from '../versions';
import { parseLayersToPreviousVersion } from './parse-layers-to-previous-version';

const parseClientToServerMap = {
    [LayerType.BACKGROUND_LAYER]: (layer: ILayerProps) => {
        const {
            id,

            /** 图层类型 */
            layerType,

            width,

            height,

            left,

            top,

            /** 旋转角度 */
            rotate,

            isAILayer,
            // 透明度
            opacity,

            /** 翻转 */
            flipH,
            flipV,

            /** 图层数据 */
            data,

            /** 是否锁定 */
            lock,

            /** 子图层 */
            children,

            /** 是否闪光 */
            shine,

            /** 图层隐藏 */
            hidden,

            /** 标签 */
            category,

            /** 图片功能 */
            mode,

            /** 父级分组id */
            parentId,

            // 图层名称
            title,

            materialIds,

            prompt,

            fitWidth,

            fitHeight,

            extraInfo,
        } = layer;

        const {
            /** 元素url */
            url,

            /** 图片拉伸模式 */
            stretchOption,

            /** 原始图片宽度 */
            naturalWidth,

            /** 原始图片高度 */
            naturalHeight,

            /** 图片裁剪 */
            imageTransform,

            backgroundColor,
            backgroundGradient,
            dynamicInfo,
        } = data as IBackgroundLayerProps;

        const newLayer = {
            id,

            /** 图层类型 */
            layerType,

            width,

            height,

            left,

            top,

            /** 旋转角度 */
            rotate,
            isAILayer,

            // 透明度
            opacity,

            /** 翻转 */
            ...(isBoolean(flipH) && { flipH }),
            ...(isBoolean(flipV) && { flipV }),

            /** 是否锁定 */
            lock: lock === undefined ? false : lock,

            /** 子图层 */
            children,

            /** 是否闪光 */
            shine,

            /** 元素url */
            url,
            /**
             * 抠图
             */
            cutoutMaskURL: '',

            /** 遮罩元素url */
            maskURL: '',

            /** 图片拉伸模式 */
            // stretchOption: maskURL ? 2 : stretchOption,
            stretchOption,

            /** 原始图片宽度 */
            naturalWidth,

            /** 原始图片高度 */
            naturalHeight,

            /** 图片裁剪 */
            imageTransform: formatImageTransformToServer(imageTransform, width, height),

            /** 图层隐藏 */
            hidden,

            /** 蒙版类型 */
            maskType: MaskType.none,

            /** 标签 */
            category,

            /** 图片功能 */
            mode,

            /** 父级分组id */
            parentId: parentId ? parentId : '',

            // 图层名称
            title,

            materialIds,

            backgroundColor,
            backgroundGradient,

            prompt,

            fitWidth,

            fitHeight,

            extraInfo,
            dynamicInfo,
        } as IServerBgLayerProps;

        return newLayer;
    },
    [LayerType.GROUP_LAYER]: (layer: ILayerProps) => {
        const {
            id,

            width,

            height,

            left,

            top,

            /** 旋转角度 */
            rotate,
            isAILayer,

            // 透明度
            opacity,

            /** 翻转 */
            flipH,
            flipV,

            lock,

            shine,

            /**图层隐藏 */
            hidden,
            /** 父级分组id */
            parentId,

            // 图层名称
            title,

            materialIds,
            children,

            prompt,

            fitWidth,

            fitHeight,
            extraInfo,
        } = layer;

        const data = layer.data as IGroupLayerProps;

        if (data.extension && data.extension?.role === GroupRoleType.PUZZLE) {
            const {
                containerLayers,
                edgeCornerRadius,
                edgeInnerWidth,
                padding,
                puzzleMode,
                backgroundLayer,
                frameScale,
            } = data.extension;
            const outerWidth = padding ? (Array.isArray(padding) ? padding[0] : padding) : 0;
            const newLayer = {
                id,

                /** 图层类型 */
                layerType: LayerType.PUZZLE,

                width,

                height,

                left,

                top,

                isAILayer,
                /** 旋转角度 */
                rotate,

                // 透明度
                opacity,

                /** 翻转 */
                ...(isBoolean(flipH) && { flipH }),
                ...(isBoolean(flipV) && { flipV }),

                shine,

                /**图层隐藏 */
                hidden,
                children: [],
                /** 父级分组id */
                parentId: parentId ? parentId : '',

                // 图层名称
                title,

                materialIds,

                isTextMaterial: (data as IGroupLayerProps).isTextMaterial,

                lock: lock === undefined ? false : lock,

                prompt,

                fitWidth,

                fitHeight,
                extraInfo,

                enableEdge: true,
                intelligentMode: 'base',
                subtitleHeightRatio: 1,
                enableSeamless: false,
                seamlessValue: 0,
                containerLayers: containerLayers.map((el) => {
                    const layer = children[el.stickerLayer];
                    if (layer && parseClientToServerMap[layer.layerType]) {
                        return {
                            left: el.left,
                            top: el.top,
                            width: el.width,
                            height: el.height,
                            stickerLayer: parseClientToServerMap[layer.layerType](layer),
                            rotate: el.rotate,
                        };
                    }
                }),
                edgeCornerRadius,
                edgeInnerWidth,
                edgeOuterWidth: outerWidth,
                puzzleMode,
                backgroundLayer,
                frameScale,
            } as IServerPuzzleLayerProps;

            return newLayer;
        }

        const newLayer = {
            id,

            /** 图层类型 */
            layerType: LayerType.GROUP_LAYER,

            width,

            height,

            left,

            top,

            isAILayer,
            /** 旋转角度 */
            rotate,

            // 透明度
            opacity,

            /** 翻转 */
            ...(isBoolean(flipH) && { flipH }),
            ...(isBoolean(flipV) && { flipV }),

            shine,

            /**图层隐藏 */
            hidden,
            /** 父级分组id */
            parentId: parentId ? parentId : '',

            // 图层名称
            title,

            materialIds,

            isTextMaterial: (data as IGroupLayerProps).isTextMaterial,

            /** 子图层 */
            children:
                (children?.map((layer: ILayerProps) => {
                    if (parseClientToServerMap[layer.layerType]) {
                        return parseClientToServerMap[layer.layerType](layer);
                    }
                    return layer;
                }) as ILayerProps[]) || [],

            lock: lock === undefined ? false : lock,

            prompt,

            fitWidth,

            fitHeight,
            extraInfo,
        } as IServerGroupLayerProps;

        return newLayer;
    },
    [LayerType.MAIN_LAYER]: (layer: ILayerProps) => {
        const {
            id,

            /** 图层类型 */
            layerType,

            width,

            height,

            left,
            isAILayer,

            top,

            /** 旋转角度 */
            rotate,

            // 透明度
            opacity,

            /** 翻转 */
            flipH,
            flipV,

            /** 图层数据 */
            data,

            /** 是否锁定 */
            lock,

            /** 是否闪光 */
            shine,

            /** 图层隐藏 */
            hidden,

            /** 标签 */
            category,

            /** 图片功能 */
            mode,

            /** 父级分组id */
            parentId,

            // 图层名称
            title,

            materialIds,

            prompt,

            fitWidth,

            fitHeight,

            extraInfo,
        } = layer;

        const {
            /** 元素url */
            url,
            /** 遮罩图片url */
            maskURL,

            /** 图片拉伸模式 */
            stretchOption,

            /** 原始图片宽度 */
            naturalWidth,

            /** 原始图片高度 */
            naturalHeight,

            /** 图片裁剪 */
            imageTransform,

            /** 蒙版类型 */
            maskType,
            /** 抠图 */
            cutoutMaskURL,

            effect,

            filter,
            hint,
            isTransparent,
        } = data as IMainLayerProps;

        const newLayer = {
            id,

            /** 图层类型 */
            layerType,

            width,

            isAILayer,
            height,

            left,

            top,

            /** 旋转角度 */
            rotate,

            // 透明度
            opacity,

            /** 翻转 */
            ...(isBoolean(flipH) && { flipH }),
            ...(isBoolean(flipV) && { flipV }),

            /** 是否锁定 */
            lock: lock === undefined ? false : lock,

            /** 是否闪光 */
            shine,

            /** 元素url */
            url,
            /**
             * 抠图
             */
            cutoutMaskURL,

            /** 遮罩元素url */
            maskURL,

            /** 图片拉伸模式 */
            // stretchOption: maskURL ? 2 : stretchOption,
            stretchOption,

            /** 原始图片宽度 */
            naturalWidth,

            /** 原始图片高度 */
            naturalHeight,

            /** 图片裁剪 */
            imageTransform: formatImageTransformToServer(imageTransform, width, height),

            effect,

            filter,

            /** 图层隐藏 */
            hidden,

            /** 蒙版类型 */
            maskType,

            /** 标签 */
            category,

            /** 图片功能 */
            mode,

            /** 父级分组id */
            parentId: parentId ? parentId : '',

            // 图层名称
            title,

            materialIds,

            children: [],

            prompt,

            hint,

            isTransparent,

            fitWidth,

            fitHeight,

            extraInfo,
        } as IServerMainLayerProps;

        return newLayer;
    },
    [LayerType.TEXT_LAYER]: (layer: ILayerProps) => {
        const {
            id,

            /** 图层类型 */
            layerType,

            width,
            isAILayer,

            height,

            left,

            top,

            /** 旋转角度 */
            rotate,

            // 透明度
            opacity,

            /** 翻转 */
            flipH,
            flipV,

            lock,

            shine,

            data,

            /**图层隐藏 */
            hidden,
            /** 父级分组id */
            parentId,

            // 图层名称
            title,

            materialIds,

            prompt,

            fitWidth,

            fitHeight,
            extraInfo,
        } = layer;

        const {
            text,
            color,
            fontSize,
            fontFamily,
            lineSpacing,
            letterSpacing,
            bold,
            italic,
            lineThrough,
            underline,
            textAlign,
            writingMode,
            textGlow,
            textShadow,
            textStroke,
            textBackground,
            textGradient,
            listStyle,
            textContents,
            shapeExtension,
            screenshotUrl,
            texture,
            textMark,
            advancedTextMaterial,
            listStyleMaterial,
        } = data as ITextLayerProps;

        const newLayer = {
            id,

            /** 图层类型 */
            layerType,

            width,

            height,

            left,

            top,

            isAILayer,
            /** 旋转角度 */
            rotate,

            // 透明度
            opacity,

            flipH: isBoolean((data as any).flipH) ? (data as any).flipH : flipH,

            flipV: isBoolean((data as any).flipV) ? (data as any).flipV : flipV,

            text: text.replace(/(\r\n|\r)/g, '\n'),

            lock: lock === undefined ? false : lock,

            shine,

            color,
            fontSize,
            fontFamily,
            lineSpacing,
            letterSpacing,
            bold,
            italic,
            lineThrough,
            underline,
            textAlign,
            writingMode,
            textGlow,
            textShadow,
            textStroke,
            /**图层隐藏 */
            hidden,
            /** 父级分组id */
            parentId: parentId ? parentId : '',

            // 图层名称
            title,

            materialIds,
            textBackground,
            textGradient,
            listStyle,

            children: [],

            prompt,
            textContents,
            shapeExtension: shapeExtension && screenshotUrl ? shapeExtension : null,
            screenshotUrl: shapeExtension && screenshotUrl ? screenshotUrl : null,
            texture,
            textMark,

            fitWidth,

            fitHeight,
            extraInfo,
            advancedTextMaterial,
            listStyleMaterial,
        } as IServerLayerProps;

        return newLayer;
    },
    [LayerType.IMAGE_LAYER]: (layer: ILayerProps) => {
        const {
            id,

            /** 图层类型 */
            layerType,

            width,

            height,

            left,
            isAILayer,

            top,

            /** 旋转角度 */
            rotate,

            // 透明度
            opacity,

            /** 翻转 */
            flipH,
            flipV,

            /** 图层数据 */
            data,

            /** 是否锁定 */
            lock,

            /** 是否闪光 */
            shine,

            /** 图层隐藏 */
            hidden,

            /** 标签 */
            category,

            /** 图片功能 */
            mode,

            /** 父级分组id */
            parentId,

            // 图层名称
            title,

            materialIds,

            prompt,

            fitWidth,

            fitHeight,
            extraInfo,
        } = layer;

        const {
            /** 元素url */
            url,
            /** 遮罩图片url */
            maskURL,

            /** 图片拉伸模式 */
            stretchOption,

            /** 原始图片宽度 */
            naturalWidth,

            /** 原始图片高度 */
            naturalHeight,

            /** 图片裁剪 */
            imageTransform,

            /** 蒙版类型 */
            maskType,
            /** 抠图 */
            cutoutMaskURL,

            effect,

            filter,
            hint,
            aiTextExtension,
            minimumMask,
            maskFeathering,
            blendMode,
            borderRadius, // 圆角
            mcpEffects,
            fill,
        } = data as IImageLayerProps;

        const newLayer = {
            id,

            /** 图层类型 */
            layerType,

            width,

            isAILayer,
            height,

            left,

            top,

            /** 旋转角度 */
            rotate,

            // 透明度
            opacity,

            /** 翻转 */
            ...(isBoolean(flipH) && { flipH }),
            ...(isBoolean(flipV) && { flipV }),

            /** 是否锁定 */
            lock: lock === undefined ? false : lock,

            /** 是否闪光 */
            shine,

            /** 元素url */
            url,
            /**
             * 抠图
             */
            cutoutMaskURL,

            /** 遮罩元素url */
            maskURL,

            /** 图片拉伸模式 */
            // stretchOption: maskURL ? 2 : stretchOption,
            stretchOption,

            /** 原始图片宽度 */
            naturalWidth,

            /** 原始图片高度 */
            naturalHeight,

            /** 图片裁剪 */
            imageTransform: formatImageTransformToServer(imageTransform, width, height),

            effect,

            filter,

            /** 图层隐藏 */
            hidden,

            /** 蒙版类型 */
            maskType,

            /** 标签 */
            category,

            /** 图片功能 */
            mode,

            /** 父级分组id */
            parentId: parentId ? parentId : '',

            // 图层名称
            title,

            materialIds,

            children: [],

            prompt,

            hint,

            aiTextExtension: aiTextExtension || null,

            fitWidth,

            fitHeight,
            minimumMask,
            maskFeathering,
            blendMode,
            extraInfo,
            borderRadius,
            mcpEffects,
            fill,
        } as IServerImageLayerProps;

        if (!newLayer.fill || !newLayer.fill.color) delete newLayer.fill;

        return newLayer;
    },
    [LayerType.WATERMARK]: (layer: ILayerProps) => {
        const {
            id,

            /** 图层类型 */
            layerType,

            width,

            height,

            left,
            isAILayer,
            top,

            /** 旋转角度 */
            rotate,

            // 透明度
            opacity,

            /** 翻转 */
            flipH,
            flipV,

            /** 图层数据 */
            data,

            /** 是否锁定 */
            lock,

            /** 是否闪光 */
            shine,

            /** 图层隐藏 */
            hidden,

            /** 标签 */
            category,

            /** 图片功能 */
            mode,

            /** 父级分组id */
            parentId,

            // 图层名称
            title,

            materialIds,

            prompt,

            fitWidth,

            fitHeight,
            extraInfo,
        } = layer;

        const {
            // 用户自定义上传的水印图片地址
            url,

            // 是否全屏水印
            isFullScreen,

            // 横向位移
            offsetX,

            // 纵向位移
            offsetY,

            // 水印大小
            size,

            // 间距
            space,

            // 交错
            cross,

            // 文字内容，有url时为空
            texts,

            // 文字颜色#FFFFFFFF，有url时为空
            textColor,

            zipUrl,

            isColorApplyToAll,

            // 字体，有url时为空
            fontFamily,
        } = data as IWatermarkLayerProps;

        const newLayer = {
            id,

            /** 图层类型 */
            layerType,
            isAILayer,

            width,

            height,

            left,

            top,

            /** 旋转角度 */
            rotate,

            // 透明度
            opacity,

            /** 翻转 */
            ...(isBoolean(flipH) && { flipH }),
            ...(isBoolean(flipV) && { flipV }),

            /** 是否锁定 */
            lock: lock === undefined ? false : lock,

            /** 是否闪光 */
            shine,

            /** 图层隐藏 */
            hidden,

            /** 父级分组id */
            parentId: parentId ? parentId : '',

            // 图层名称
            title,

            materialIds,

            url,

            zipUrl,

            // 是否全屏水印
            isFullScreen,

            // 横向位移
            offsetX,

            // 纵向位移
            offsetY,

            // 水印大小
            size,

            // 间距
            space,

            // 交错
            cross,

            // 文字内容，有url时为空
            texts,

            // 文字颜色#FFFFFFFF，有url时为空
            textColor,

            // 字体，有url时为空
            fontFamily,

            children: [],

            prompt,

            fitWidth,

            fitHeight,
            extraInfo,
            isColorApplyToAll,
        } as IServerWatermarkLayerProps;

        return newLayer;
    },
    [LayerType.VECTOR_LAYER]: (layer: ILayerProps) => {
        const {
            id,

            /** 图层类型 */
            layerType,

            width,

            height,

            left,

            top,

            /** 旋转角度 */
            rotate,

            // 透明度
            opacity,

            /** 翻转 */
            flipH,
            flipV,

            /** 图层数据 */
            data,

            /** 是否锁定 */
            lock,

            /** 是否闪光 */
            shine,

            /** 图层隐藏 */
            hidden,

            /** 标签 */
            category,

            /** 图片功能 */
            mode,

            /** 父级分组id */
            parentId,

            // 图层名称
            title,

            materialIds,

            prompt,
            isAILayer,

            fitWidth,

            fitHeight,
            extraInfo,
        } = layer;

        const {
            url,
            imageTransform,
            stretchOption,
            naturalWidth,
            naturalHeight,
            vectorType,
            colors,
            fill,
            fillGradient,
            radius,
            stroke,
            strokeGradient,
            strokeWidth,
            strokeDasharray,
            strokeLinecap,
            pickerIndex,
            effect,
            blur,
            strokeHidden,
            borderRadius,
            textInfo,
        } = data as IVectorLayerProps;

        const newLayer = {
            url,
            imageTransform,
            stretchOption,
            naturalWidth,
            naturalHeight,
            vectorType,
            isAILayer,
            colors,
            fill,
            fillGradient: fillGradient || null,
            radius,
            stroke,
            strokeGradient: strokeGradient || null,
            strokeWidth,
            strokeDasharray,
            strokeLinecap,
            pickerIndex: pickerIndex,
            effect,
            blur,
            strokeHidden,

            id,
            layerType,
            left,
            top,
            rotate,
            opacity,
            width,
            height,
            shine,
            lock,
            hidden,
            /** 翻转 */
            ...(isBoolean(flipH) && { flipH }),
            ...(isBoolean(flipV) && { flipV }),
            mode,
            category,
            parentId: parentId ? parentId : '',
            title,
            materialIds,
            prompt,
            children: [],

            fitWidth,

            fitHeight,
            extraInfo,
            borderRadius,
            textInfo,
        } as IServerVectorLayerProps;

        return newLayer;
    },
    [LayerType.QR_CODE]: (layer: ILayerProps) => {
        const {
            id,

            /** 图层类型 */
            layerType,

            width,

            height,

            left,

            top,

            /** 旋转角度 */
            rotate,

            // 透明度
            opacity,

            /** 翻转 */
            flipH,
            flipV,

            /** 图层数据 */
            data,

            /** 是否锁定 */
            lock,

            /** 是否闪光 */
            shine,

            /** 图层隐藏 */
            hidden,

            // /** 标签 */
            // category,

            // /** 图片功能 */
            // mode,

            /** 父级分组id */
            parentId,

            // 图层名称
            title,

            materialIds,

            prompt,

            fitWidth,

            fitHeight,
            extraInfo,
        } = layer;

        const { imageTransform, stretchOption, text, official, fill, stroke, strokeGradient } =
            data as IQrCodeLayerProps;

        const newLayer = {
            imageTransform,
            stretchOption,
            text,
            official,
            fill,
            stroke,
            strokeGradient: strokeGradient || null,

            id,
            layerType,
            left,
            top,
            rotate,
            opacity,
            width,
            height,
            shine,
            lock,
            hidden,
            /** 翻转 */
            ...(isBoolean(flipH) && { flipH }),
            ...(isBoolean(flipV) && { flipV }),
            // mode,
            // category,
            parentId: parentId ? parentId : '',
            title,
            materialIds,
            prompt,
            children: [],

            fitWidth,

            fitHeight,
            extraInfo,
        } as IServerQrCodeLayerProps;

        return newLayer;
    },
    [LayerType.TABLE]: (layer: ILayerProps) => {
        const {
            id,

            /** 图层类型 */
            layerType,

            width,
            isAILayer,

            height,

            left,

            top,

            /** 旋转角度 */
            rotate,

            // 透明度
            opacity,

            /** 翻转 */
            flipH,
            flipV,

            lock,

            shine,

            data,

            /**图层隐藏 */
            hidden,
            /** 父级分组id */
            parentId,

            // 图层名称
            title,

            materialIds,

            prompt,

            fitWidth,

            fitHeight,

            extraInfo,
        } = layer;

        if (checkTableLayerPropsIsVersion15_0_0(data)) {
            const {
                screenshotUrl,
                cells,

                baseCells,
                borders,
            } = data;

            const newLayer = {
                id,

                /** 图层类型 */
                layerType,

                width,

                height,

                left,

                top,

                isAILayer,
                /** 旋转角度 */
                rotate,

                // 透明度
                opacity,

                lock: lock === undefined ? false : lock,

                shine,

                screenshotUrl,
                cells,
                /** 预设相关的数据 */

                baseCells,
                borders,
                /**图层隐藏 */
                hidden,
                /** 父级分组id */
                parentId: parentId ? parentId : '',

                // 图层名称
                title,

                materialIds,

                children: [],

                prompt,

                fitWidth,

                fitHeight,
                extraInfo,
            } as IServerTableLayerProps__15_0_0;

            return newLayer;
        }

        const { screenshotUrl, allStyleId, cells, baseCells, styles, presetId } =
            data as unknown as ITableLayerProps<true>;

        const newLayer = {
            id,

            /** 图层类型 */
            layerType,

            width,

            height,

            left,

            top,

            isAILayer,
            /** 旋转角度 */
            rotate,

            // 透明度
            opacity,

            /** 翻转 */
            ...(isBoolean(flipH) && { flipH }),
            ...(isBoolean(flipV) && { flipV }),

            lock: lock === undefined ? false : lock,

            shine,

            screenshotUrl,
            allStyleId,
            cells,
            baseCells,
            styles,
            presetId,

            /**图层隐藏 */
            hidden,
            /** 父级分组id */
            parentId: parentId ? parentId : '',

            // 图层名称
            title,

            materialIds,

            children: [],

            prompt,

            fitWidth,

            fitHeight,

            extraInfo,
        } as IServerTableLayerProps;

        return newLayer;
    },
    [LayerType.BRUSH_LAYER]: (layer: ILayerProps) => {
        const {
            id,

            /** 图层类型 */
            layerType,

            width,

            height,

            left,

            top,

            /** 旋转角度 */
            rotate,

            // 透明度
            opacity,

            /** 翻转 */
            flipH,
            flipV,

            /** 图层数据 */
            data,

            /** 是否锁定 */
            lock,

            /** 是否闪光 */
            shine,

            /** 图层隐藏 */
            hidden,

            /** 父级分组id */
            parentId,

            // 图层名称
            title,

            materialIds,

            prompt,

            fitWidth,

            fitHeight,
            extraInfo,
        } = layer;

        const {
            brushType,
            url,
            fills,
            stroke,
            startPoint,
            endPoint,
            radius,
            imageTransform,
            stretchOption,
            naturalWidth,
            naturalHeight,
            screenshotUrl,
        } = data as IBrushLayerProps;

        const newLayer = {
            brushType,
            url,
            fills,
            stroke,
            startPoint,
            endPoint,
            radius,
            imageTransform,
            stretchOption,
            naturalWidth,
            naturalHeight,
            screenshotUrl: screenshotUrl || '',

            id,
            layerType,
            left,
            top,
            rotate,
            opacity,
            width,
            height,
            shine,
            lock,
            hidden,
            /** 翻转 */
            ...(isBoolean(flipH) && { flipH }),
            ...(isBoolean(flipV) && { flipV }),

            parentId: parentId ? parentId : '',
            title,
            materialIds,
            prompt,
            children: [],

            fitWidth,

            fitHeight,
            extraInfo,
        } as IServerBrushLayerProps;

        return newLayer;
    },
    [LayerType.MAGNIFIER]: (layer: ILayerProps) => {
        const {
            id,

            /** 图层类型 */
            layerType,

            width,

            height,

            left,

            top,

            /** 旋转角度 */
            rotate,
            isAILayer,

            // 透明度
            opacity,

            /** 翻转 */
            flipH,
            flipV,

            lock,

            shine,

            /**图层隐藏 */
            hidden,
            /** 父级分组id */
            parentId,

            // 图层名称
            title,

            materialIds,
            children,

            data,

            prompt,

            fitWidth,

            fitHeight,
            extraInfo,
        } = layer;

        const {
            url,

            size,

            hFlip,

            strokeColor,

            strokeSize,

            enableOffset,

            x,

            y,

            scale,

            enableStroke,
        } = data as IMagnifierLayerProps;

        const newLayer = {
            id,

            /** 图层类型 */
            layerType,

            width,

            height,

            left,

            top,

            isAILayer,
            /** 旋转角度 */
            rotate,

            // 透明度
            opacity,

            /** 翻转 */
            ...(isBoolean(flipH) && { flipH }),
            ...(isBoolean(flipV) && { flipV }),

            shine,

            /**图层隐藏 */
            hidden,
            /** 父级分组id */
            parentId: parentId ? parentId : '',

            // 图层名称
            title,

            materialIds,

            /** 子图层 */
            children: [],

            lock: lock === undefined ? false : lock,

            prompt,

            fitWidth,

            fitHeight,
            extraInfo,
            url,

            size,

            hFlip,

            strokeColor,

            strokeSize,

            enableOffset,

            x,

            y,

            scale,

            enableStroke,
        } as IMagnifierLayerProps;

        return newLayer;
    },
    [LayerType.MARKUP_LAYER]: (layer: ILayerProps) => {
        const {
            id,

            /** 图层类型 */
            layerType,

            width,

            height,

            left,

            top,

            /** 旋转角度 */
            rotate,

            // 透明度
            opacity,

            /** 翻转 */
            flipH,
            flipV,

            /** 图层数据 */
            data,

            /** 是否锁定 */
            lock,

            /** 是否闪光 */
            shine,

            /** 图层隐藏 */
            hidden,

            /** 父级分组id */
            parentId,

            // 图层名称
            title,

            materialIds,

            prompt,

            fitWidth,

            fitHeight,
            extraInfo,
        } = layer;

        const {
            markupType,
            label,
            anchor,
            centerOffset,
            hasLine,
            fill,
            startPoint,
            endPoint,
            imageTransform,
            screenshotUrl,
        } = data as IMarkupLayerProps;

        const newLayer = {
            markupType,
            label,
            anchor,
            centerOffset,
            hasLine,
            fill,
            startPoint,
            endPoint,
            imageTransform,
            screenshotUrl: screenshotUrl || '',

            id,
            layerType,
            left,
            top,
            rotate,
            opacity,
            width,
            height,
            shine,
            lock,
            hidden,
            /** 翻转 */
            ...(isBoolean(flipH) && { flipH }),
            ...(isBoolean(flipV) && { flipV }),

            parentId: parentId ? parentId : '',
            title,
            materialIds,
            prompt,
            children: [],

            fitWidth,

            fitHeight,
            extraInfo,
        } as IServerMarkupLayerProps;

        return newLayer;
    },
    [LayerType.MEDIA_LAYER]: (layer: ILayerProps) => {
        const {
            id,

            /** 图层类型 */
            layerType,

            width,

            height,

            left,

            top,

            /** 旋转角度 */
            rotate,

            // 透明度
            opacity,

            /** 翻转 */
            flipH,
            flipV,

            /** 图层数据 */
            data,

            /** 是否锁定 */
            lock,

            /** 是否闪光 */
            shine,

            /** 图层隐藏 */
            hidden,

            /** 父级分组id */
            parentId,

            // 图层名称
            title,

            materialIds,

            prompt,

            fitWidth,

            fitHeight,
            extraInfo,
        } = layer;

        const {
            url,
            imageTransform,
            mediaType,
            naturalWidth,
            naturalHeight,
            autoplay,
            loop,
            controls,
            borderRadius,
            maskURL,
            poster,
        } = data as IMediaLayerProps;

        const newLayer = {
            url,
            imageTransform,
            mediaType,
            naturalWidth,
            naturalHeight,
            autoplay,
            loop,
            controls,
            borderRadius,
            maskURL,
            poster,

            id,
            layerType,
            left,
            top,
            rotate,
            opacity,
            width,
            height,
            shine,
            lock,
            hidden,
            /** 翻转 */
            ...(isBoolean(flipH) && { flipH }),
            ...(isBoolean(flipV) && { flipV }),

            parentId: parentId ? parentId : '',
            title,
            materialIds,
            prompt,
            children: [],

            fitWidth,

            fitHeight,
            extraInfo,
        } as IServerMediaLayerProps;

        return newLayer;
    },
    [LayerType.CHART]: (layer: ILayerProps) => {
        const {
            id,

            /** 图层类型 */
            layerType,

            width,
            isAILayer,

            height,

            left,

            top,

            /** 旋转角度 */
            rotate,

            // 透明度
            opacity,

            /** 翻转 */
            flipH,
            flipV,

            lock,

            shine,

            data,

            /**图层隐藏 */
            hidden,
            /** 父级分组id */
            parentId,

            // 图层名称
            title,

            materialIds,

            prompt,

            fitWidth,

            fitHeight,
            extraInfo,
            children,
        } = layer;

        const {
            screenshotUrl,

            type,

            chartData,
            /** 颜色数组, 长度必须与图表的系列数一致 */
            colors,
            grid,
            legend,
            /** 类别坐标轴(对应chartData中的项目). 饼图时, 该值不生效. 但必须有值, 用于图表类型切换后的展示 */
            categoryAxisLabel,
            /** 值坐标轴, 同类别坐标轴 */
            valueAxisLabel,
            series,
        } = data as IChartLayerProps;

        const newLayer = {
            screenshotUrl,

            type,

            chartData,
            /** 颜色数组, 长度必须与图表的系列数一致 */
            colors,
            grid,
            legend,
            /** 类别坐标轴(对应chartData中的项目). 饼图时, 该值不生效. 但必须有值, 用于图表类型切换后的展示 */
            categoryAxisLabel,
            /** 值坐标轴, 同类别坐标轴 */
            valueAxisLabel,
            series,

            id,
            children,

            /** 图层类型 */
            layerType,

            width,
            isAILayer,

            height,

            left,

            top,

            /** 旋转角度 */
            rotate,

            // 透明度
            opacity,

            /** 翻转 */
            flipH,
            flipV,

            lock,

            shine,

            /**图层隐藏 */
            hidden,
            /** 父级分组id */
            parentId,

            // 图层名称
            title,

            materialIds,

            prompt,

            fitWidth,

            fitHeight,
            extraInfo,
        } as IServerChartLayerProps;

        return newLayer;
    },
};

export const formatImageTransformToServer = (
    imageTransform: IImageTransform,
    layerWidth: number,
    layerHeight: number,
) => {
    const { offsetX: offsetXRatio, offsetY: offsetYRatio, widthRatio, heightRatio, scale } = imageTransform;
    const offsetX = (offsetXRatio * widthRatio * scale * layerWidth) / layerWidth;
    const offsetY = (offsetYRatio * heightRatio * scale * layerHeight) / layerHeight;

    imageTransform.offsetX = offsetX;
    imageTransform.offsetY = offsetY;
    return cloneDeep(imageTransform);
};

/**
 * 格式化颜色值
 */
export function formatColorVal(obj: any) {
    for (const key in obj) {
        if (obj[key] instanceof Object) {
            formatColorVal(obj[key]);
        } else {
            if (key === 'color' || key === 'stroke') {
                const color = tinycolor(obj[key]);
                if (key === 'stroke' && (!obj[key] || !color)) {
                    return;
                }
                // console.log('color--' + key, 'before:' + obj[key], 'after:' + color.toHex8String());
                if (color.isValid()) {
                    obj[key] = color.toHex8String();
                } else {
                    obj[key] = '#000000ff';
                }
            }
        }
    }
}

export const parseClientLayersToServer = (
    clientLayers: ILayerProps[],
    is900Version: boolean,
    validateData = false,
    topStruct: ITopStruct = {},
) => {
    const serverLayers = clientLayers.map((layer) => {
        if (parseClientToServerMap[layer.layerType]) {
            return parseClientToServerMap[layer.layerType](layer);
        }
        return layer;
    }) as IServerLayerProps[];

    const result = parseLayersToPreviousVersion(serverLayers, is900Version, LATEST_VERSION, topStruct);

    if (validateData) {
        const validate = validateTemplate(result.version as VERSION, result.layers);
        if (validate !== true) {
            console.log('版本验证失败模板==', JSON.stringify(result));
            throw new Error('版本验证失败');
        }
    }

    return result;
};
